import { Settings } from "./settings";

// Helper: converts multiplier-only config into pixel values
const buildPaddingConfig = (configs) =>
  Object.fromEntries(
    Object.entries(configs).map(([key, { sides, top, bottom }]) => [
      key,
      {
        paddingSides: Settings.SYMBOL_WIDTH * sides,
        paddingTop: Settings.SYMBOL_HEIGHT * top,
        paddingBottom: Settings.SYMBOL_HEIGHT * bottom,
      },
    ]),
  );

export const PixiSettings = {
  // Multipliers only — auto-multiplied by SYMBOL_WIDTH (sides) and SYMBOL_HEIGHT (top/bottom)
  REELSET_LAYOUT_CONFIGS: buildPaddingConfig({
    MOBILE_SMALL_PORTRAIT: { sides: 1.0, top: 1.0, bottom: 1.0 },
    MOBILE_SMALL_LANDSCAPE: { sides: 3.1, top: 1.0, bottom: 0.5 },
    MOBILE_PORTRAIT: { sides: 1.0, top: 3.5, bottom: 4.0 },
    MOBILE_LANDSCAPE: { sides: 3.25, top: 0.5, bottom: 1.0 },
    TABLET_PORTRAIT: { sides: 3, top: 3.5, bottom: 3.5 },
    TABLET_LANDSCAPE: { sides: 3.75, top: 1.0, bottom: 1.0 },
    SMALL_DESKTOP: { sides: 4.0, top: 1.0, bottom: 1.0 },
    DESKTOP: { sides: 1.5, top: 1.0, bottom: 1.0 },
  }),

  ORIENTATION: {
    PORTRAIT: "PORTRAIT",
    LANDSCAPE: "LANDSCAPE",
  },

  DEVICE: {
    MOBILE_SMALL: "MOBILE_SMALL",
    MOBILE: "MOBILE",
    TABLET: "TABLET",
    DESKTOP: "DESKTOP",
  },

  /**
   * Centralized font style presets.
   * Only visual appearance (fill, stroke, gradient, shadow) — not size/weight/layout.
   * Change these once per game to restyle all text across every component.
   */
  FONT_STYLES: {
    /**
     * PRIMARY — Two-layer stacked text (shadow bottom + white top).
     * Used by: WinAnimation, IntroPopup, BuyBonusController
     */
    PRIMARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      shadow: {
        fill: ["#7b4cbf", "#5a2bb0", "#7b4cbf"],
        fillGradientType: 0,
        stroke: "#3a1878",
        strokeThickness: 4,
        lineJoin: "round",
      },
      top: {
        fill: "#ffffff",
        stroke: "#3a1878",
        strokeThickness: 3,
        lineJoin: "round",
      },
      shadowOffsetY: 3,
    },

    /**
     * SECONDARY — Single-layer text with drop shadow.
     * Used by: ReelFrontController (LINES text)
     */
    SECONDARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      fill: "#d6dbff",
      stroke: "#ffffff",
      strokeThickness: 2,
      dropShadow: true,
      dropShadowColor: "#085fac",
      dropShadowDistance: 3,
      dropShadowBlur: 0,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
    },

    /**
     * SLOT_TEXT — Shared layout preset for all spine-slot-attached text.
     * Defines fontWeight, fontSize, padding, and resolution so every
     * slot text (IntroPopup cards, max win, tap-to-start, WinAnimation button)
     * looks identical. Spread PRIMARY first, then SLOT_TEXT to override layout props.
     */
    SLOT_TEXT: {
      fontWeight: "400",
      fontSize: 38,
      align: "center",
      letterSpacing: 0,
      padding: 10,
      resolution: 1,
    },
  },
};

/**
 * Helper: returns true if the device is any mobile phone type (MOBILE or MOBILE_SMALL).
 * Use this in direct conditionals where MOBILE_SMALL should behave the same as MOBILE.
 * For per-device configs, use getResponsiveConfig which auto-falls back MOBILE_SMALL → MOBILE.
 */
export const isMobileDevice = (device) =>
  device === PixiSettings.DEVICE.MOBILE ||
  device === PixiSettings.DEVICE.MOBILE_SMALL;
